import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    HostListener,
    AfterViewInit,
    Renderer2,
    OnDestroy,
    AfterContentInit,
    ContentChildren,
    QueryList,
} from '@angular/core';
import { AceModule } from '../ACE/ace.module';
import { DirectivesModule } from '../Directives/directives.module';
import { DraggableElementSearchDirection } from '../Directives/Draggable/draggable.directive';
import { HelpModule } from '../Help-Tooltip/help.module';
import { MessageModalService } from '../Message-Box/messageModal.service';
import { ModalFooterDirective } from './modalFooter.directive';

/**
 * Simple wrapper for providing header and footer content to modals
 */
@Component({
    selector: 'ws-modal-wrapper',
    templateUrl: './modalWrapper.component.html',
    styleUrls: ['./modalWrapper.component.scss'],
    imports: [CommonModule, AceModule, DirectivesModule, HelpModule],
})
export class ModalWrapperComponent implements AfterContentInit, AfterViewInit, OnDestroy {
    constructor(
        private readonly _messageModalService: MessageModalService,
        private readonly _renderer2: Renderer2,
        private readonly _elementRef: ElementRef<HTMLElement>,
    ) {}

    @Input() headerLabel: string;
    @Input() headerHelpContentId: string;
    @Input() isHeaderLink: boolean;
    @Input() subHeaderLabel: string;
    @Input() headerIcon: string;
    @Input() headerIconHelpContentId: string;
    @Input() footerLabel: string;
    @Input() cancelLabel: string = 'Cancel';
    @Input() acceptLabel: string = 'OK';
    @Input() auxButtonLabel: string;
    @Input() cancelClass: string = 'btn-secondary';
    @Input() acceptClass: string = 'btn-primary';
    @Input() auxButtonClass: string = 'btn-secondary';
    @Input() bodyClass: string;
    @Input() disableSave: boolean = false;
    @Input() disableCancel: boolean = false;
    @Input() disableAuxButton: boolean = false;
    @Input() acceptHelpContentId: string;
    @Input() cancelHelpContentId: string;
    @Input() auxButtonHelpContentId: string;
    @Input() hideSave: boolean = false;
    @Input() hideCancel: boolean = false;
    @Input() hideCancelX: boolean = false;
    @Input() hideAuxButton: boolean = false;
    @Input() hideFooter: boolean = false;
    @Input() tabindex: number = 0;
    @Input() suppressKeyboardEvents: boolean;
    @Input() excludeModalStyling: boolean;
    @Input() hideBackdrop: boolean;
    @Input() backdropClick: boolean;
    @Input() disableMove: boolean;
    @Input() useAceStyles: boolean;
    @Input() loading: boolean;
    @Input() ignoreCloseOnRoute: boolean;
    @Input() isEditing: boolean | (() => Promise<boolean>);

    @Output() save: EventEmitter<void> = new EventEmitter();
    @Output() cancel: EventEmitter<boolean> = new EventEmitter();
    @Output() auxButton: EventEmitter<void> = new EventEmitter();
    @Output() headerClick: EventEmitter<void> = new EventEmitter();

    searchDirection = DraggableElementSearchDirection;

    /** True when the consumer projected custom footer content using the `[modal-footer]` slot. */
    hasCustomFooter = false;

    @ContentChildren(ModalFooterDirective, { descendants: true })
    private readonly _customFooters?: QueryList<ModalFooterDirective>;

    // Combined handler for Enter and Escape on window keyup
    @HostListener('window:keyup', ['$event'])
    async _windowKeyupCombined(event: KeyboardEvent) {
        if (this.suppressKeyboardEvents) {
            return;
        }

        if (!this.isInActiveModal()) {
            return;
        }

        const key = event.key || (event as any).which || (event as any).keyCode;
        const isEnter = key === 'Enter' || key === 13 || key === '13';
        const isEscape = key === 'Escape' || key === 'Esc' || key === 27 || key === '27';

        if (isEnter || isEscape) {
            event.preventDefault();
            event.stopPropagation();

            if (isEnter) {
                this.enterPressed();
            }

            if (isEscape) {
                await this.cancelClicked();
            }
        }
    }

    @HostListener('window:hashchange')
    locationEvent() {
        // If the user navigates away from the page (browser back button), we want to cancel the modal
        // This is the only time true is passed
        // The boolean allows handling situations where the modal should be shown globally, regardless of user navigation
        if (!this.ignoreCloseOnRoute) {
            this.cancel.emit(true);
        }
    }

    @HostListener('document:click', ['$event'])
    async _documentClick(event: MouseEvent) {
        const target = event.target as HTMLElement | null;
        if (!target || !this.backdropClick) {
            return;
        }

        // modal specific "backdrop" area
        if (target.tagName.toLowerCase() !== 'modal-container') {
            return;
        }

        // Don't allow clicks on other modal buttons, etc
        if (!target.contains(this._elementRef.nativeElement)) {
            return;
        }

        await this.cancelClicked();
    }

    async cancelClicked(): Promise<void> {
        try {
            if (this.isEditing instanceof Function) {
                await this.isEditing();
            } else if (this.isEditing) {
                await this._messageModalService.prompt(
                    'You have unsaved changes. Are you sure you want to cancel?',
                    'Unsaved Changes',
                );
            }
        } catch (e) {
            return;
        }
        this.cancel.emit(false);
    }

    ngAfterContentInit(): void {
        this._updateHasCustomFooter();
        this._customFooters?.changes.subscribe(() => this._updateHasCustomFooter());
    }

    ngAfterViewInit(): void {
        if (this.backdropClick) {
            this._renderer2.addClass(document.body, 'single-modal');
        }
    }

    ngOnDestroy(): void {
        if (this.backdropClick) {
            this._renderer2.removeClass(document.body, 'single-modal');
        }
    }

    enterPressed(): void {
        if (!this.disableSave) {
            this.save.emit();
        }
    }

    headerClicked(): void {
        if (this.isHeaderLink) {
            this.headerClick.emit();
        }
    }

    private _updateHasCustomFooter(): void {
        this.hasCustomFooter = (this._customFooters?.length ?? 0) > 0;
    }

    private isInActiveModal(): boolean {
        const host = this._elementRef.nativeElement;
        const modal = host.closest('.modal');

        if (modal) {
            const style = window.getComputedStyle(modal);

            return (
                modal.classList.contains('show') &&
                style.display !== 'none' &&
                style.visibility !== 'hidden'
            );
        }

        const modalContainer = host.closest('modal-container');

        if (!modalContainer) {
            return false;
        }

        const style = window.getComputedStyle(modalContainer);

        return style.display !== 'none' && style.visibility !== 'hidden';
    }
}
